import React, { useState, useEffect, useRef } from 'react';
import { SendHorizontal, SquarePen, Store, DatabaseZap, ShieldCheck, Tags, Target, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { type Edge } from 'reactflow'; // Import React Flow Edge type
import useStore from '../../store/useStore';
import { useActionSnackbar } from '../../contexts/ActionSnackbarContext'; // Import snackbar hook
import { templateService } from '../../services/template.service'; // Import template service
import { saveProjectId } from '../../store/persistence'; // Import saveProjectId
import { CustomNode } from '../../types'; // Import CustomNode
import env from '../../utils/env'; // Import shared env config
import { generateCanvasSummaryString, generateClearCanvasActions } from '../../utils/canvasUtils'; // Import utility functions
import { getDefaultNodeWidth, NODE_DIMENSIONS } from '../../utils/nodeHelpers'; // Import node dimension helpers
import type {
  AICanvasUpdateResponse,
  CanvasUpdateAction,
  CanvasNode, 
  CanvasEdge,
  OpenTemplatePayload // Import the new payload type
} from '../../../backend/src/types/ai';

interface Message {
  id: string;
  text: string;
  sender: 'assistant' | 'user';
  timestamp: Date;
  isTyping?: boolean; // Flag to indicate if the message is currently being typed
}

interface SuggestedReply {
  id: string;
  text: string;
  icon: React.ElementType;
}
interface WelcomeMessage {
  id: string;
  text: string;
}

const welcomeMessages: WelcomeMessage[] = [
  { id: '1', text: 'Hi! I’m here to help you plan your ideal server-side data architecture — just tell me what you’re working on.' },
  { id: '2', text: 'Welcome! Let’s build the right server-side data flow for your business. Start by telling me your goals.' },
  { id: '3', text: 'I’ll help you design a clean, secure server-side setup. What’s your current stack or pain point?' },
  { id: '4', text: 'Need help with server-side tagging, compliance, or data control? Let’s map the right solution.' },
  { id: '5', text: 'Looking to improve compliance, performance, or data governance? I’ll help you build the right data infrastructure.' },
  { id: '6', text: 'Let’s design a data flow that’s fast, compliant, and future-proof. What’s your current setup like?' },
  { id: '7', text: 'Want to scale your Retail Media, CDP, or privacy strategy? Let’s build the right foundation.' },
  { id: '8', text: 'Hi — I’ll help you plan a privacy-first, server-side architecture. What platforms are you using today?' },
  { id: '9', text: 'Tell me your current tools, tags, and data goals — I’ll help architect a server-side solution.' },
  { id: '10', text: 'Looking to move to server-side tagging? I can help map everything out step by step.' },
  { id: '11', text: 'I’ll help you build a reliable, scalable data routing layer. What cloud or region are you working with?' },
  { id: '12', text: 'Hey there — want better control over your customer data? I’ll help you plan the right architecture.' },
];

// Maximum number of API response messages allowed in a chat (excluding the initial welcome message)
const MAX_MESSAGES = 5;

// TypeWriter component for streaming effect
const TypeWriter: React.FC<{ text: string; isTyping: boolean; onComplete?: () => void }> = ({ text, isTyping, onComplete }) => {
  const [displayedText, setDisplayedText] = useState(isTyping ? '' : text); // Start empty only if typing
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false); // Prevent multiple onComplete calls

  useEffect(() => {
    // Only run typing effect if isTyping is true and text is not fully displayed yet
    if (isTyping && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 10); // Speed of typing (reduced from 15ms)

      return () => clearTimeout(timeout);
    } else if (isTyping && currentIndex >= text.length && onComplete && !hasCompleted.current) {
      // If typing was active and is now complete
      hasCompleted.current = true;
      onComplete();
    } else if (!isTyping && displayedText !== text) {
       // If isTyping became false externally (or was initially false), ensure full text is shown
       setDisplayedText(text);
    }
    // Remove displayedText from dependency array to prevent infinite loop
  }, [currentIndex, text, onComplete, isTyping]); 

  // Render the current state of displayed text
  return <>{displayedText}</>;
};


// --- Create initial message outside the component ---
const createStableInitialMessage = () => {
  const getRandomWelcomeMessage = () => {
    const randomIndex = Math.floor(Math.random() * welcomeMessages.length);
    return welcomeMessages[randomIndex];
  };
  return {
    id: `init-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // Ensure more unique ID
    text: getRandomWelcomeMessage().text,
    sender: 'assistant' as const,
    timestamp: new Date(), // Timestamp will still be creation time
    isTyping: true,
  };
};
// --- End initial message creation ---

// Module-level flag to track if the initial welcome animation has played once
let initialWelcomeAnimationHasPlayedOnce = false;
// Module-level flag for other initial element animations (suggested replies, alpha box, input)
let otherInitialElementsAnimatedIn = false;

interface AIAssistantProps {
  isActive: boolean;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ isActive }) => {
  // State for welcome message animation control
  const [messages, setMessages] = useState<Message[]>(() => {
    const initialMsg = createStableInitialMessage();
    // Only set isTyping to true if the global flag indicates it hasn't played yet
    initialMsg.isTyping = !initialWelcomeAnimationHasPlayedOnce;
    return [initialMsg];
  });

  // Store the ID of the current initial message to correctly update the global flag
  const initialMessageIdRef = useRef(messages[0].id);

  // State to track if non-welcome animations have run in this session for this instance
  const [hasAnimatedInThisSession] = useState(otherInitialElementsAnimatedIn);

  const inputRef = useRef<HTMLTextAreaElement>(null); // Ref for the textarea field
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const { showSnackbar } = useActionSnackbar(); // Get snackbar function
  const [isLoading, setIsLoading] = useState(false);
  const [apiMessageCount, setApiMessageCount] = useState(0); // Start with 0 (not counting welcome message)
  const prevIsActive = useRef(isActive); // Track previous isActive state
  const prevIsLoading = useRef(isLoading); // Track previous isLoading state

  // Effect to mark that initial animations (other than welcome message typing) have run once globally
  useEffect(() => {
    if (!hasAnimatedInThisSession) {
      // This instance is performing the initial animation set.
      // Mark it globally after animations are expected to complete.
      const longestAnimationTime = 1000; // ms, estimate for suggested replies container + buttons
      const timer = setTimeout(() => {
        otherInitialElementsAnimatedIn = true;
        // No need to setHasAnimatedInThisSession here as it's initialized from the global flag
        // and won't change for this instance after mount.
      }, longestAnimationTime);
      return () => clearTimeout(timer);
    }
  }, [hasAnimatedInThisSession]); // Runs once if hasAnimatedInThisSession is initially false.

  // Update API message count when messages change
  useEffect(() => {
    // Count only API response messages (excluding the initial welcome message)
    // We count pairs of messages (user message + assistant response)
    const apiMessages = messages.filter(
      (msg, index) => index > 0 && msg.sender === 'assistant'
    );
    setApiMessageCount(apiMessages.length);
  }, [messages]);
  
  // Function to clear/reset the chat
  const handleClearChat = () => {
    const newWelcomeMessage = createStableInitialMessage();
    newWelcomeMessage.isTyping = true; // New chats should always animate the welcome message
    setMessages([newWelcomeMessage]);
    initialMessageIdRef.current = newWelcomeMessage.id; // Update ref to the new initial message's ID
    setInputValue('');
    setIsLoading(false);
    setApiMessageCount(0); // Reset to 0 as we're only counting API responses
    setShowSuggestions(true); // Ensure suggestions are shown for a new chat
  };

  const suggestedReplies: SuggestedReply[] = [
    { id: '1', text: "I want to move third-party tags server-side", icon: DatabaseZap },
    { id: '2', text: "Can MetaRouter make our CDP data faster and cleaner?", icon: Users },
    { id: '3', text: "How do we better enforce data governance across vendors?", icon: ShieldCheck },
    { id: '4', text: "Client-side tags are slowing down our site", icon: Tags },
    { id: '5', text: "We need to improve our ad measurement and attribution.", icon: Target },
    { id: '6', text: "How can we unlock insights from anonymous user data?", icon: Store },
  ];
  
  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;
    
    // Check if we've reached the message limit
    if (apiMessageCount >= MAX_MESSAGES) {
      // Don't allow more messages
      return;
    }
    
    const newMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages([...messages, newMessage]);
    setInputValue('');
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    // Call the new function to handle API call
    callApi(inputValue);
  };
  
  const handleSuggestedReply = (reply: string) => {
    // Check if we've reached the message limit
    if (apiMessageCount >= MAX_MESSAGES) {
      // Don't allow more messages
      return;
    }
    
    const newMessage: Message = {
      id: Date.now().toString(),
      text: reply,
      sender: 'user',
      timestamp: new Date(),
    };
    
    setMessages([...messages, newMessage]);
    // Reset textarea height after sending a suggested reply as well
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    // Call the new function to handle API call
    callApi(reply);
  };

  const callApi = async (userInput: string) => {
    setIsLoading(true);
    try {
      const { nodes, edges } = useStore.getState();
      const canvasSummary = generateCanvasSummaryString(nodes as CustomNode[], edges as Edge[]);
      
      // Create a structured canvas state object to send to the backend
      const currentCanvasState = {
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: node.data
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle,
          label: edge.label
        }))
      };

      // Special case for "clear canvas" command
      if (userInput.toLowerCase().trim() === 'clear the canvas' || 
          userInput.toLowerCase().trim() === 'clear canvas') {
        // Generate clear canvas actions directly without calling the API
        const clearActions = generateClearCanvasActions(nodes as CustomNode[], edges as Edge[]);
        
        // Create a response similar to what the API would return
        const clearCanvasResponse: AICanvasUpdateResponse = {
          message: "Cleared all nodes and edges from the canvas.",
          actions: clearActions
        };
        
        // Display the message
        const assistantTextMessage: Message = {
          id: `text-${Date.now().toString()}`,
          text: clearCanvasResponse.message,
          sender: 'assistant',
          timestamp: new Date(),
          isTyping: true, // Start with typing animation
        };
        setMessages((prevMessages) => [...prevMessages, assistantTextMessage]);
        
        // Process the clear actions
        const store = useStore.getState();
        const { reactFlowInstance } = store; // Get reactFlowInstance
        // Take a snapshot before clearing
        store.takeSnapshot();
        
        // Process all delete actions
        clearActions.forEach((action: CanvasUpdateAction) => {
          if (action.action === 'deleteNode' && action.payload && 'id' in action.payload) {
            store.deleteNode((action.payload as { id: string }).id);
          } else if (action.action === 'deleteEdge' && action.payload && 'id' in action.payload) {
            store.deleteEdge((action.payload as { id: string }).id);
          }
        });

        // Fit view after clearing
        setTimeout(() => {
          reactFlowInstance?.fitView({ padding: 0.2, duration: 200 });
        }, 0);
        
        setIsLoading(false);
        return; // Skip the API call
      }

      // Use the centralized environment variable for the backend URL
      const backendUrl = env.apiUrl;
      // Ensure no double slashes if apiUrl ends with / and path starts with /
      const apiUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
      const response = await fetch(`${apiUrl}/api/ai/aiAssistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userInput, 
          canvasSummary,
          currentCanvasState 
        }),
      });

      if (!response.ok) {
        // Try to get error message from response body
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // Ignore if response is not JSON
        }
        console.error('API Error:', response.status, response.statusText, errorData);
        // Add a generic error message to chat for the user
        const errorMessage: Message = {
          id: Date.now().toString(),
          text: `Error: Could not get a response. Status: ${response.status}. Please check the console for more details.`,
          sender: 'assistant',
          timestamp: new Date(),
          isTyping: true, // Start with typing animation
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
        return; // Exit early on error
      }

      const data: AICanvasUpdateResponse = await response.json();

      // Display the textual message from AI
      if (data.message) {
        const assistantTextMessage: Message = {
          id: `text-${Date.now().toString()}`,
          text: data.message,
          sender: 'assistant',
          timestamp: new Date(),
          isTyping: true, // Start with typing animation
        };
        setMessages((prevMessages) => [...prevMessages, assistantTextMessage]);
      }

      // Process canvas actions
      if (data.actions && data.actions.length > 0) {
        const store = useStore.getState();
        // Use a for...of loop to allow await inside
        for (const action of data.actions) { 
          switch (action.action) {
            case 'addNode':
              if (action.payload && 'id' in action.payload && 'type' in action.payload && 'position' in action.payload && 'data' in action.payload) {
                const payload = action.payload as CanvasNode;
                
                // Set default width if not provided by AI
                if (payload.width === undefined || payload.width === null) {
                  payload.width = getDefaultNodeWidth(payload.type);
                }

                // Set default height if not provided by AI
                if (payload.height === undefined || payload.height === null) {
                  // For group nodes, use MAX_HEIGHT, otherwise use MIN_HEIGHT
                  payload.height = payload.type === 'group' ? NODE_DIMENSIONS.MAX_HEIGHT : NODE_DIMENSIONS.MIN_HEIGHT;
                }

                // Add default handles if they don't exist
                if (!payload.data.handles || !Array.isArray(payload.data.handles) || payload.data.handles.length === 0) {
                  // Add default input handle (target)
                  payload.data.handles = [
                    {
                      id: 'default-target',
                      type: 'target',
                      position: 'left',
                      color: '#22c55e', // Green color for input
                    }
                  ];
                  
                  // For most node types, also add an output handle (source)
                  // Don't add output handle for destination nodes which are typically endpoints
                  if (payload.type !== 'destination') {
                    payload.data.handles.push({
                      id: 'default-source',
                      type: 'source',
                      position: 'right',
                      color: '#3b82f6', // Blue color for output
                    });
                  }
                }
                
                store.addNode(payload);
              } else {
                console.error('Invalid payload for addNode:', action.payload);
              }
              break;
            case 'updateNode':
              if (action.payload && 'id' in action.payload) {
                const { id, ...updateProperties } = action.payload as Partial<CanvasNode>;
                if ('data' in updateProperties && typeof updateProperties.data === 'object' && updateProperties.data !== null) {
                  store.updateNode(id as string, updateProperties.data as object);
                } else {
                  store.updateNode(id as string, updateProperties as object);
                }
              } else {
                console.error('Invalid payload for updateNode:', action.payload);
              }
              break;
            case 'deleteNode':
              if (action.payload && 'id' in action.payload) {
                store.deleteNode((action.payload as { id: string }).id);
              } else {
                console.error('Invalid payload for deleteNode:', action.payload);
              }
              break;
            case 'addEdge':
              if (action.payload && 'id' in action.payload && 'source' in action.payload && 'target' in action.payload) {
                const newEdgeFromAI = action.payload as CanvasEdge;
                const edgeToAdd: Edge = {
                  ...newEdgeFromAI,
                  id: newEdgeFromAI.id || `edge-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                  type: newEdgeFromAI.type || 'smoothstep', 
                  label: newEdgeFromAI.label,
                  sourceHandle: newEdgeFromAI.sourceHandle || '',
                  targetHandle: newEdgeFromAI.targetHandle || '',
                  ...(newEdgeFromAI.hasOwnProperty('data') && (newEdgeFromAI as any).data ? { data: (newEdgeFromAI as any).data } : { data: {} }),
                };
                store.setEdges([...store.edges, edgeToAdd]);
              } else {
                console.error('Invalid payload for addEdge:', action.payload);
              }
              break;
            case 'updateEdge':
              if (action.payload && 'id' in action.payload) {
                const updatedEdgeFromAI = action.payload as Partial<CanvasEdge>;
                const edgeIdToUpdate = (action.payload as {id: string}).id;
                store.setEdges(
                  store.edges.map((existingEdge: Edge) => {
                    if (existingEdge.id === edgeIdToUpdate) {
                      const newEdgeProps: Edge = {
                        ...existingEdge,
                        ...updatedEdgeFromAI,
                        label: updatedEdgeFromAI.label !== undefined ? updatedEdgeFromAI.label : existingEdge.label,
                        sourceHandle: updatedEdgeFromAI.sourceHandle !== undefined ? updatedEdgeFromAI.sourceHandle : existingEdge.sourceHandle || '',
                        targetHandle: updatedEdgeFromAI.targetHandle !== undefined ? updatedEdgeFromAI.targetHandle : existingEdge.targetHandle || '',
                        data: {
                          ...(existingEdge.data || {}),
                          ...(updatedEdgeFromAI.hasOwnProperty('data') && (updatedEdgeFromAI as any).data ? (updatedEdgeFromAI as any).data : {}),
                        }
                      };
                      return newEdgeProps;
                    }
                    return existingEdge;
                  })
                );
              } else {
                console.error('Invalid payload for updateEdge:', action.payload);
              }
              break;
            case 'deleteEdge':
              if (action.payload && 'id' in action.payload) {
                store.deleteEdge((action.payload as { id: string }).id);
              } else {
                console.error('Invalid payload for deleteEdge:', action.payload);
              }
              break;
            case 'openTemplate':
              if (action.payload && 'templateId' in action.payload) {
                const { templateId } = action.payload as OpenTemplatePayload;
                showSnackbar("Loading template...");
                try {
                  const template = await templateService.getTemplate(templateId);
                  if (template && template.nodes && template.edges) {
                    // Update the store directly
                    const { setNodes, setEdges, reactFlowInstance } = useStore.getState();
                    setNodes(template.nodes);
                    setEdges(template.edges);
                    saveProjectId(null); 
                    setTimeout(() => {
                      reactFlowInstance?.fitView({ padding: 0.2, duration: 200 });
                    }, 0);
                  } else {
                    console.error('Invalid template data received from service:', template);
                    showSnackbar(`Failed to load template '${templateId}': Invalid data.`);
                  }
                } catch (loadError) {
                  console.error(`Error loading template ${templateId} via AI:`, loadError);
                  showSnackbar(`Failed to load template '${templateId}'.`); // Show error snackbar
                  // Add error message to chat
                  const errorMessage: Message = {
                    id: `error-${Date.now().toString()}`,
                    text: `Sorry, I couldn't load the template '${templateId}'. There was an error retrieving its data.`,
                    sender: 'assistant',
                    timestamp: new Date(),
                    isTyping: true,
                  };
                  setMessages((prevMessages) => [...prevMessages, errorMessage]);
                }
              } else {
                console.error('Invalid payload for openTemplate:', action.payload);
              }
              break;
            default:
              console.warn('Unknown canvas action:', action);
          }
        } // End for...of loop
        // Take snapshot *after* processing all actions from one AI response
        store.takeSnapshot(); 

        // Fit view after AI actions
        setTimeout(() => {
          const { reactFlowInstance } = store; // store is in scope from when it was defined at the start of this block
          reactFlowInstance?.fitView({ padding: 0.2, duration: 200 });
        }, 0);
      } else if (!data.message) { // If no message and no actions, it's an unexpected response
        console.error('API Error: Unexpected response format from backend. No message or actions.', data);
        const errorMessage: Message = {
          id: Date.now().toString(),
          text: 'Error: Received an empty or unexpected response from the server.',
          sender: 'assistant',
          timestamp: new Date(),
          isTyping: true, // Start with typing animation
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }

    } catch (error) {
      console.error('Failed to send/process message to API:', error);
        const errorMessage: Message = {
          id: Date.now().toString(),
          text: 'Error: Failed to connect to the AI assistant. Please check your network or contact support.',
          sender: 'assistant',
          timestamp: new Date(),
          isTyping: true, // Start with typing animation
        };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
      // Refocus logic is now handled by the useEffect watching `isLoading`
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Reference to scroll to bottom of messages
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Effect to focus input when tab becomes active
  useEffect(() => {
    if (isActive && !prevIsActive.current && inputRef.current && !isLoading && apiMessageCount < MAX_MESSAGES) {
      inputRef.current.focus();
    }
    prevIsActive.current = isActive; // Update previous state after checking
  }, [isActive, isLoading, apiMessageCount]);

  // Effect to focus input after API call finishes
  useEffect(() => {
    // If loading has just finished (was true, now false)
    if (prevIsLoading.current && !isLoading) {
      if (inputRef.current && apiMessageCount < MAX_MESSAGES) {
        inputRef.current.focus();
      }
    }
    prevIsLoading.current = isLoading; // Update previous isLoading state for the next render
  }, [isLoading, apiMessageCount]); // Depend on isLoading and apiMessageCount
  
  // Function to mark AI message as done typing
  const handleTypingComplete = (messageId: string) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, isTyping: false } : msg
      )
    );
    // If the completed message was the initial one and the global flag isn't set, set it.
    if (messageId === initialMessageIdRef.current && !initialWelcomeAnimationHasPlayedOnce) {
      initialWelcomeAnimationHasPlayedOnce = true;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="absolute right-2 top-12">
        {/* Clear chat button */}
        <motion.button
          onClick={handleClearChat}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Start New Chat"
          transition={{ duration: 0.3 }}
          >
            <SquarePen size={18} />
          </motion.button>
        </div>
      {/* Message display area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              // Animate user messages from bottom slightly
              initial={ message.sender === 'user' ? { opacity: 0, y: 15 } : { opacity: 0 } } 
              animate={ message.sender === 'user' ? { opacity: 1, y: 0 } : { opacity: 1 } }
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.25, // Slightly longer duration for user message entry
                ease: "easeOut" // Add ease-out
              }}
              className={`flex ${
                message.sender === 'assistant' ? 'justify-start' : 'justify-end'
                }`}
              >
                <div
                  className={`max-w-[90%] text-md ${
                    message.sender === 'assistant'
                      ? 'rounded-xl text-gray-800 dark:text-gray-200 px-2 py-3' 
                      : 'bg-gray-200 dark:bg-gray-200/10 border border-gray-300 dark:border-gray-100/10 text-gray-700 dark:text-gray-300 pl-4 pr-4 pt-2 pb-3 rounded-xl rounded-br-none' // Added rounded-br-none for user
                  }`}
                >
                  {/* Use TypeWriter for assistant messages */}
                  {message.sender === 'assistant' ? (
                    <TypeWriter
                      text={message.text}
                      isTyping={!!message.isTyping} // Pass isTyping status, ensure boolean
                      onComplete={() => handleTypingComplete(message.id)}
                    />
                  ) : (
                    message.text // User messages remain plain text
                  )}
                </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Loading Indicator (Blinking Rectangle) */}
        {isLoading && messages.length > 0 && messages[messages.length - 1].sender === 'user' && (
          <motion.div
            className="flex justify-start mt-1" // Align left like assistant messages
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-4 h-4 bg-gray-500 animate-pulse rounded-full opacity-75"></div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />

      </div>
      
      {/* Suggested replies (New Location & Style) */}
      <AnimatePresence mode="wait">
        {messages.length === 1 && showSuggestions && (
          <motion.div
            className="px-3 pb-2" // Added padding
            initial={!hasAnimatedInThisSession ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={
              !hasAnimatedInThisSession
                ? { delay: 0.5, duration: 0.3, exit: { duration: 0.1 } }
                : { duration: 0, exit: { duration: 0.1 } } // Instant entry, normal exit
            }
          >
            <div className="flex flex-nowrap overflow-x-auto items-stretch gap-2 py-1">
              {suggestedReplies.map((reply, index) => (
                <motion.button
                  key={reply.id}
                  onClick={() => handleSuggestedReply(reply.text)}
                  className="flex flex-col items-center justify-center p-2 border border-gray-200 hover:bg-gray-500/10 dark:border-gray-500/30 dark:hover:bg-gray-600/10 text-gray-800 dark:text-gray-200 dark:hover:text-emerald-200 rounded-lg transition-colors aspect-square shrink-0 basis-28"
                  initial={!hasAnimatedInThisSession ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={
                    !hasAnimatedInThisSession
                      ? { delay: 0.2 + index * 0.05, duration: 0.2 }
                      : { duration: 0 }
                  }
                  whileTap={{ scale: 0.98 }}
                >
                  <reply.icon size={16} className="mb-1" />
                  <span className="text-xs text-center">{reply.text}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input area */}
      <div className="p-3">
        <div className="flex items-end gap-2"> {/* Changed items-center to items-end to align button with bottom of textarea */}
          <div className="relative flex-1">
          <motion.textarea
            ref={inputRef} // Assign the ref to the textarea element
            rows={1}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Auto-resize logic
              if (inputRef.current) {
                inputRef.current.style.height = 'auto'; // Reset height
                inputRef.current.style.height = `${inputRef.current.scrollHeight}px`; // Set to scroll height
              }
              if (e.target.value.trim() !== '') {
                setShowSuggestions(false);
              } else {
                setShowSuggestions(true);
                // Reset to single row if input is cleared
                if (inputRef.current) {
                  inputRef.current.style.height = 'auto'; 
                }
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              apiMessageCount >= MAX_MESSAGES 
                ? "Message limit reached. Start a new chat." 
                : isLoading 
                  ? "Assistant is thinking..." 
                  : "Type a message..."
            }
            className="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 resize-none overflow-y-auto max-h-32" // Added rounded-lg, resize-none, overflow-y-auto, max-h-32
            disabled={isLoading || apiMessageCount >= MAX_MESSAGES}
            initial={!hasAnimatedInThisSession ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={!hasAnimatedInThisSession ? { duration: 0.3 } : { duration: 0 }}
          />
          <motion.button
            onClick={handleSendMessage}
            className="absolute right-3 bottom-[18px] text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-500" // Adjusted positioning: right-3, bottom-2.5
            disabled={isLoading || apiMessageCount >= MAX_MESSAGES || inputValue.trim() === ''}
            whileTap={{ scale: 0.9 }}
          >
            {isLoading ? (
              <div className="rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            ) : (
              <SendHorizontal size={18} />
            )}
          </motion.button>
          </div>
        </div>
        
      </div>
    </div>
  );
};

// Wrap the component with React.memo to prevent unnecessary re-renders
const MemoizedAIAssistant = React.memo(AIAssistant);
export default MemoizedAIAssistant;
